import React, { useEffect, useRef, useState } from 'react';
import { cn, addClasses, mergeClasses } from '#lib/helpers/class.js';
import { Row, RowTitle } from '../planner/row.jsx';
import { makeSortable } from '#@planner/planner/sortable.jsx';
import { DragHandleContext, DragHandle } from '../planner/sortable.jsx';
import { Icon } from '@iconify-icon/react';
import checkbox from '#lib/components/checkbox.jsx';
import FloatingTooltip from '#components/tooltip.jsx';
import { WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { isFunction } from '#lib/utils/function.js';
import { SuggestionIcon } from '#components/suggestion-icon.jsx';
import { sameIdOrImportId } from './helpers.js';
import { useBool } from '#lib/hooks/use-boolean-state.js';

const { Full: Checkbox } = checkbox;

function _PlaceRow(props, ref) {
    const {
        as = 'div',
        name,
        onSelectDndPlace,
        onSelectPlace,
        onEdit,
        onSelectAll,
        onDeleteLocation,
        onDeletePlace,
        isPlaceSelected,
        style,
        children,
        place,
        plans
    } = props;

    // Gestione 3-state
    const multiSelectRef = useRef();
    const isAllSelected = place.info.every((el) => el.checked) && place.suggestions.every((el) => el.checked);
    const isSomeSelected =
        isAllSelected || place.info?.some((item) => item.checked) || place.suggestions?.some((item) => item.checked);
    useEffect(() => {
        if (!multiSelectRef.current || !place) return;
        // se sono tutte selezionate
        if (isAllSelected) {
            multiSelectRef.current.indeterminate = false;
            return;
        }
        // se almeno una è selezionata (ma non tutte)
        if (isSomeSelected && !isAllSelected) {
            multiSelectRef.current.indeterminate = true;
            return;
        }
        // se nessuna è selezionata
        multiSelectRef.current.indeterminate = false;
        return;
    }, [isAllSelected, isSomeSelected, multiSelectRef]);
    //TODO: creare Hook per i places e passarli da sopra?

    // const handleSelectAll = React.useCallback(() => {
    //     onSelectAll(isSomeChecked);
    // }, [place, onEdit, name]);
    const isMatched = plans?.some((v) => sameIdOrImportId(place, v?.suggestionId) && v.type === 'suggestion');
    const handleLock = React.useCallback(() => {
        const { info, suggestions } = place;
        for (const item of info) {
            if (item.checked) onEdit(name, 'info', 'locked', true, item.id);
        }
        for (const item of suggestions) {
            if (item.checked && !isMatched) onEdit(name, 'suggestions', 'locked', true, item.id);
        }
    }, [place, onEdit, name]);

    const handleUnlock = React.useCallback(() => {
        const { info, suggestions } = place;
        for (const item of info) {
            if (item.checked) onEdit(name, 'info', 'locked', false, item.id);
        }
        for (const item of suggestions) {
            if (item.checked && isMatched) onEdit(name, 'suggestions', 'locked', false, item.id);
        }
    }, [place, onEdit, name]);

    const handleDelete = React.useCallback(() => {
        const { info, suggestions } = place;
        const confirm = window.confirm(_t.global.confirmDelete());
        if (!confirm) return;
        for (const item of info) {
            if (item.checked && !item.locked) onDeletePlace(name, 'info', item.id);
        }
        for (const item of suggestions) {
            if (item.checked && !item.locked) onDeletePlace(name, 'suggestions', item.id);
        }
    }, [place, onDeletePlace, name]);

    const handleDeletePlace = React.useCallback(() => {
        const { info, suggestions } = place;
        const results = [...info, ...suggestions];
        const isSomeLocked = results.some((el) => el.locked);
        const confirm = window.confirm(isSomeLocked ? _t.edit.places.warningDelete() : _t.global.confirmDelete());
        if (!confirm) return;
        if (isSomeLocked) {
            results.forEach((el) => {
                if (!el.locked) onDeletePlace(name, el.type, el.id);
            });
            return;
        }
        onDeleteLocation(name);
    }, [onDeleteLocation, place, name]);

    const [isHovering, onMouseOver, onMouseOut] = useBool(false);
    const [isFocusing, onFocus, onBlur] = useBool(false);

    return (
        <Row
            ref={ref}
            as={as}
            className="relative flex flex-col items-center justify-between border-none col-span-full"
            style={{ ...style }}
            onMouseOver={onSelectDndPlace}
        >
            <div
                className={cn(
                    'w-full relative flex justify-between items-center gap-x-4 border-b cursor-default pr-2',
                    isFocusing && 'border-b-neutral-900',
                    isHovering && !isFocusing && 'border-b-neutral-400'
                )}
                onMouseOver={onMouseOver}
                onMouseOut={onMouseOut}
                onFocus={onFocus}
                onBlur={onBlur}
            >
                <InputTextArea
                    value={name}
                    rows={1}
                    typeInput="title"
                    placeholder={_t.edit.places.newPlace()}
                    onChange={(e) => {
                        e.stopPropagation();
                        onEdit(name, null, 'name', e.target.value);
                    }}
                    className={addClasses('hover:border-transparent focus:border-transparent font-semibold')}
                />
                <div className="flex gap-x-[8px]">
                    <FloatingTooltip content={_t.global.delete()}>
                        <Icon
                            icon="ph:trash"
                            width="1.3rem"
                            height="1.3rem"
                            className="cursor-pointer"
                            onClick={handleDeletePlace}
                        />
                    </FloatingTooltip>

                    <FloatingTooltip
                        content={isPlaceSelected ? _t.edit.places.closeTools() : _t.edit.places.openTools()}
                    >
                        {isPlaceSelected ? (
                            <XMarkIcon className="w-5 h-5 cursor-pointer" onClick={onSelectPlace} />
                        ) : (
                            <WrenchScrewdriverIcon className="w-5 h-5 cursor-pointer" onClick={onSelectPlace} />
                        )}
                    </FloatingTooltip>
                </div>
            </div>
            <ToolsButtons
                checkboxRef={multiSelectRef}
                className={addClasses(
                    cn(
                        'transition-all duration-500',
                        isPlaceSelected ? 'h-full p-1 opacity-1' : 'h-0 px-1 opacity-0 overflow-hidden'
                    )
                )}
                place={place}
                handleSelectAll={onSelectAll}
                handleLock={handleLock}
                handleUnlock={handleUnlock}
                handleDelete={handleDelete}
            />
            <div className="w-full py-1">{children}</div>
        </Row>
    );
}

export const PlaceRow = React.forwardRef(_PlaceRow);

function _NewPlaceRow(props, ref) {
    const { as = 'div', name, onClick, className, ...rest } = props;

    return (
        <Row
            as={as}
            ref={ref}
            className={addClasses(
                mergeClasses(
                    'relative col-span-full flex flex-col space-y-2 font-semibold justify-between border-none mb-4',
                    className
                )
            )}
        >
            <div
                className={cn(
                    'w-full px-1 py-1 flex justify-between items-center gap-x-4 border-b cursor-pointer hover:text-primary-500'
                )}
                onClick={onClick}
            >
                <div className="flex items-center justify-start w-full gap-x-6">
                    <span>{_t.edit.places.newPlace()}</span>
                </div>
                <FloatingTooltip content={_t.edit.places.newPlace()}>
                    <Icon
                        className="cursor-pointer text-primary-500 pt-[2px]"
                        icon="material-symbols-light:add"
                        width="1.6rem"
                        height="1.6rem"
                    />
                </FloatingTooltip>
            </div>
        </Row>
    );
}

export const NewPlaceRow = React.forwardRef(_NewPlaceRow);

function ToolsButtons(props) {
    const { className, place, handleSelectAll, handleLock, handleUnlock, handleDelete, checkboxRef } = props;
    const { info, suggestions } = place;
    const isAllSelected = info?.every((item) => item.checked) && suggestions?.every((item) => item.checked);

    return (
        <div
            className={mergeClasses('w-full pr-1 flex justify-between items-center gap-x-2 bg-neutral-100', className)}
        >
            <div className="flex gap-x-2">
                <b>{_t.edit.places.tools()}:</b>
            </div>
            <div className="flex gap-x-2">
                <FloatingTooltip content={_t.edit.places.unlockSelected()}>
                    <Icon
                        icon="material-symbols:lock-open-right-outline-rounded"
                        width="1.2rem"
                        height="1.2rem"
                        onClick={handleUnlock}
                        className="cursor-pointer pt-[1px]"
                    />
                </FloatingTooltip>
                <FloatingTooltip content={_t.edit.places.lockSelected()}>
                    <Icon
                        icon="material-symbols:lock-outline"
                        width="1.2rem"
                        height="1.2rem"
                        onClick={handleLock}
                        className="cursor-pointer pt-[1px]"
                    />
                </FloatingTooltip>
                <FloatingTooltip content={_t.global.delete()}>
                    <Icon
                        icon="ph:trash"
                        width="1.3rem"
                        height="1.3rem"
                        onClick={handleDelete}
                        className="cursor-pointer pt-[1px]"
                    />
                </FloatingTooltip>
                <FloatingTooltip content={_t.global.selectAll()}>
                    <input
                        type="checkbox"
                        ref={checkboxRef}
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        className="row-span-2 h-4 w-4 translate-y-0.5 cursor-pointer rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                    />
                </FloatingTooltip>
            </div>
        </div>
    );
}

function _ItemRow(props, ref) {
    const {
        as = 'div',
        placeName,
        name,
        itemId,
        importId,
        selected,
        onEdit,
        onSelect,
        onSelectSuggestion,
        onDelete,
        checked,
        locked,
        type,
        category,
        style,
        children,
        className,
        isPlaceSelected,
        hasError,
        onLock,
        isDragging,
        plans,
        ...rest
    } = props;

    const handleSelect = React.useCallback(() => {
        onSelect(itemId);
    }, [onSelect, itemId]);

    const handleLocked = React.useCallback(() => {
        onEdit(placeName, type, 'checked', !checked, itemId);
    }, [onEdit, itemId, checked, placeName, type]);

    let isMatched;
    if (type === 'suggestions') {
        isMatched = plans?.some(
            (v) =>
                v?.suggestionId &&
                sameIdOrImportId(
                    {
                        id: itemId,
                        importId: importId ?? ''
                    },
                    v?.suggestionId
                )
        );
    }
    if (type == 'info') {
        isMatched = plans?.some(
            (v) =>
                v?.infoId &&
                sameIdOrImportId(
                    {
                        id: itemId,
                        importId: importId ?? ''
                    },
                    v?.infoId
                )
        );
    }

    return (
        <Row
            as={as}
            ref={ref}
            style={{ ...style }}
            className={cn('relative flex-col pl-2 py-1 gap-1 items-center -mt-px cursor-pointer pl-2 pr-1 pt-1')}
            selected={selected}
            onMouseOver={onSelectSuggestion}
        >
            <div className={cn('w-full flex justify-between items-center gap-x-6')}>
                <div
                    className={cn(
                        'w-full flex justify-start items-center gap-x-2 hover:text-primary-600',
                        selected && !locked && 'text-primary-600',
                        (locked || isMatched) && 'text-neutral-400'
                    )}
                    onClick={handleSelect}
                    {...rest}
                >
                    {type === 'info' ? (
                        <Icon icon="material-symbols:info-outline-rounded" />
                    ) : (
                        <SuggestionIcon category={category} />
                    )}
                    {name && <RowTitle className={className}>{name}</RowTitle>}
                </div>
                <ItemButtons
                    {...props}
                    isPlaceSelected={isPlaceSelected}
                    id={itemId}
                    onDelete={onDelete}
                    onEditChecked={handleLocked}
                    hasError={hasError?.length > 0}
                    isMatched={isMatched}
                />
            </div>
            <div className="w-full">{children}</div>
        </Row>
    );
}

export const ItemRow = React.forwardRef(_ItemRow);
export const SortableItemRow = makeSortable(ItemRow);

function ItemButtons(props) {
    const {
        id,
        importId,
        placeName,
        type,
        isPlaceSelected,
        selected,
        checked,
        locked,
        plans,
        onEdit,
        onEditChecked,
        onLock,
        onDelete,
        overlay,
        hasError,
        isMatched
    } = props;

    const dragHandleProps = React.useContext(DragHandleContext);
    const [unlocked, setUnlocked] = useState(false);
    // const isMatched = plans
    //     ?.filter((plan) => plan.type === 'suggestion' || plan.type === 'info')
    //     ?.some(
    //         (v) => v?.suggestionId === id || v?.suggestionId === importId || v?.infoId === id || v?.infoId === importId
    //     );
    React.useEffect(() => {
        if (!isFunction(onEdit)) return;
        if (!isMatched) {
            setUnlocked(false);
            onLock(placeName, type, 'locked', false, id);
        } else if (unlocked === false) {
            setUnlocked(true);
            onLock(placeName, type, 'locked', true, id);
        }
    }, [plans?.length]);

    const lockItem = React.useCallback(
        (value) => {
            if (value == false && isMatched) return;
            onEdit(placeName, type, 'locked', value, id);
        },
        [isMatched /* onEdit, id, name, type */]
    );

    if (overlay) return null;
    return (
        <div className={cn('flex items-center gap-x-[6px]', isPlaceSelected && 'gap-x-[9px] pr-[4px]')}>
            {hasError && (
                <FloatingTooltip content={_t.global.invalidFieldInside()}>
                    <Icon
                        icon="ic:round-error-outline"
                        width="1.2rem"
                        height="1.2rem"
                        className="mt-[6px] text-red-500"
                    />
                </FloatingTooltip>
            )}

            <div onClick={!isMatched && locked ? () => lockItem(false) : null}>
                <FloatingTooltip
                    content={
                        !isMatched
                            ? locked
                                ? _t.edit.places.unlockSelected()
                                : _t.edit.places.lockSelected()
                            : _t.edit.places.cannotUnlock()
                    }
                >
                    <Icon
                        icon={
                            locked
                                ? 'material-symbols:lock-outline'
                                : 'material-symbols:lock-open-right-outline-rounded'
                        }
                        width="1.2rem"
                        height="1.2rem"
                        onClick={!isMatched ? () => lockItem(!locked) : null}
                        className={cn('pt-[5px]', !isMatched ? 'cursor-pointer' : 'cursor-default text-neutral-400')}
                    />
                </FloatingTooltip>
            </div>

            <FloatingTooltip content={!isMatched ? _t.global.delete() : _t.edit.places.cannotDelete()}>
                <Icon
                    icon="ph:trash"
                    width="1.3rem"
                    height="1.3rem"
                    className={cn(
                        'cursor-pointer pt-[5px]',
                        (isMatched || locked) && '!cursor-default text-neutral-400'
                    )}
                    onClick={() => {
                        !isMatched && !locked && onDelete(id);
                    }}
                />
            </FloatingTooltip>
            {isPlaceSelected ? (
                <Checkbox
                    className=""
                    checked={checked}
                    onChange={onEditChecked}
                    id={id}
                    name="locked"
                    type="checkbox"
                />
            ) : (
                <FloatingTooltip content={_t.global.drag()}>
                    <DragHandle className="pt-1 rounded cursor-grab" selected={selected} {...dragHandleProps} />
                </FloatingTooltip>
            )}
        </div>
    );
}

export function InputTextArea(props) {
    const { ref, value, rows, placeholder, className, onChange } = props;

    const _onChange = (e) => {
        // se l'evento è un keypress Invio faccio unfocus e ritorno
        if (e.nativeEvent.inputType === 'insertLineBreak') {
            e.target.blur();
            return;
        }
        onChange(e);
    };

    return (
        <textarea
            ref={ref}
            rows={rows}
            className={mergeClasses(
                cn(
                    'py-1 w-full resize-none overflow-hidden text-ellipsis border-b border-transparent hover:border-gray-300 focus:border-gray-500 focus:outline-none'
                ),
                className
            )}
            placeholder={placeholder}
            value={value}
            onChange={_onChange}
        />
    );
}
